import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import copy from '../copy';

type AuthMode = 'login' | 'register' | 'password-reset' | 'success';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  context?: 'checkout' | 'general';
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess, context = 'general' }) => {
  const navigate = useNavigate();
  const { login, register, resetPassword, isLoading } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para formularios
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [resetEmail, setResetEmail] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await login(loginData);
    if (success) {
      const successMessage = context === 'checkout' 
        ? copy.auth.login.successCheckout 
        : copy.auth.login.success;
      setSuccess(successMessage);
      setMode('success');
    } else {
      setError(copy.auth.login.error);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (registerData.password !== registerData.confirmPassword) {
      setError(copy.auth.register.passwordMismatch);
      return;
    }
    
    const success = await register(registerData);
    if (success) {
      const successMessage = context === 'checkout' 
        ? copy.auth.register.successCheckout 
        : copy.auth.register.success;
      setSuccess(successMessage);
      setMode('success');
    } else {
      setError(copy.auth.register.error);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await resetPassword(resetEmail);
    if (success) {
      setSuccess(copy.auth.passwordReset.sent);
      setMode('success');
    } else {
      setError(copy.auth.passwordReset.error);
    }
  };

  const goToCampus = () => {
    window.open(copy.auth.login.moodleUrl, '_blank');
    onClose();
  };

  const goToDashboard = () => {
    navigate('/dashboard');
    onClose();
  };

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  };

  if (!isOpen) return null;

  const renderLoginForm = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{copy.auth.login.title}</h2>
          <p className="text-gray-600">{copy.auth.login.subtitle}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.login.email}
          </label>
          <input
            type="email"
            value={loginData.email}
            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.login.password}
          </label>
          <input
            type="password"
            value={loginData.password}
            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        {error && <p className="text-red-600 text-sm">{error}</p>}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Cargando...</span>
            </>
          ) : (
            <span>{copy.auth.login.button}</span>
          )}
        </button>
      </form>
      
      <div className="mt-4 text-center">
        <button
          onClick={() => setMode('password-reset')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.login.forgot}
        </button>
      </div>
      
      <div className="mt-4 text-center">
        <span className="text-gray-600 text-sm">{copy.auth.login.noAccount} </span>
        <button
          onClick={() => setMode('register')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.login.register}
        </button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{copy.auth.register.title}</h2>
          <p className="text-gray-600">{copy.auth.register.subtitle}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handleRegister} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {copy.auth.register.firstName}
            </label>
            <input
              type="text"
              value={registerData.firstName}
              onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {copy.auth.register.lastName}
            </label>
            <input
              type="text"
              value={registerData.lastName}
              onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.email}
          </label>
          <input
            type="email"
            value={registerData.email}
            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.phone}
          </label>
          <input
            type="tel"
            value={registerData.phone}
            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.password}
          </label>
          <input
            type="password"
            value={registerData.password}
            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.confirmPassword}
          </label>
          <input
            type="password"
            value={registerData.confirmPassword}
            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        {error && <p className="text-red-600 text-sm">{error}</p>}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Cargando...</span>
            </>
          ) : (
            <span>{copy.auth.register.button}</span>
          )}
        </button>
      </form>
      
      <div className="mt-4 text-center">
        <span className="text-gray-600 text-sm">{copy.auth.register.hasAccount} </span>
        <button
          onClick={() => setMode('login')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.register.login}
        </button>
      </div>
    </div>
  );

  const renderPasswordResetForm = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{copy.auth.passwordReset.title}</h2>
          <p className="text-gray-600">{copy.auth.passwordReset.subtitle}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handlePasswordReset} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.passwordReset.email}
          </label>
          <input
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        {error && <p className="text-red-600 text-sm">{error}</p>}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Cargando...</span>
            </>
          ) : (
            <span>{copy.auth.passwordReset.button}</span>
          )}
        </button>
      </form>
      
      <div className="mt-4 text-center">
        <button
          onClick={() => setMode('login')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.passwordReset.back}
        </button>
      </div>
    </div>
  );

  const renderSuccessView = () => (
    <div className="text-center">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1"></div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="text-green-600 text-6xl mb-4">✓</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{success}</h2>
      
      <div className="space-y-3">
        {context === 'checkout' ? (
          // Solo mostrar "Continuar" en el contexto de checkout
          <button
            onClick={handleSuccess}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Continuar con el pago
          </button>
        ) : (
          // Mostrar todas las opciones en contexto general
          <>
            <button
              onClick={goToCampus}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors font-medium"
            >
              {copy.auth.login.goToCampus}
            </button>
            
            <button
              onClick={goToDashboard}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Ver Dashboard
            </button>
            
            <button
              onClick={handleSuccess}
              className="w-full bg-gray-600 text-white py-3 px-4 rounded-md hover:bg-gray-700 transition-colors font-medium"
            >
              Continuar
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {mode === 'login' && renderLoginForm()}
          {mode === 'register' && renderRegisterForm()}
          {mode === 'password-reset' && renderPasswordResetForm()}
          {mode === 'success' && renderSuccessView()}
        </div>
      </div>
    </div>
  );
};

export default AuthModal; 